import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const ROServices = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const services = [
    {
      id: 1,
      title: t.roService1Title,
      description: t.roService1Desc,
      image: '',
      features: [
        t.roService1Feature1,
        t.roService1Feature2,
        t.roService1Feature3,
        t.roService1Feature4,
        t.roService1Feature5,
        t.roService1Feature6
      ],
      icon: '🏠'
    },
    {
      id: 2,
      title: t.roService2Title,
      description: t.roService2Desc,
      image: '',
      features: [
        t.roService2Feature1,
        t.roService2Feature2,
        t.roService2Feature3,
        t.roService2Feature4,
        t.roService2Feature5,
        t.roService2Feature6
      ],
      icon: '🏢'
    },
    {
      id: 3,
      title: t.roService3Title,
      description: t.roService3Desc,
      image: '',
      features: [
        t.roService3Feature1,
        t.roService3Feature2,
        t.roService3Feature3,
        t.roService3Feature4,
        t.roService3Feature5,
        t.roService3Feature6
      ],
      icon: '🔧'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white via-blue-50 to-white relative overflow-hidden">
      {/* Water Wave Background */}
      <div className="absolute top-0 left-0 w-full opacity-5">
        <svg viewBox="0 0 1440 320" preserveAspectRatio="none" className="w-full h-64">
          <path fill="rgba(59, 130, 246, 0.5)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-block px-6 py-2 bg-blue-100 border-2 border-blue-400 rounded-full mb-6">
            <span className="text-blue-700 font-semibold font-mono text-sm tracking-wider">{t.roServicesBadge}</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
            {t.roServicesTitle} <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">{t.roServicesTitle2}</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            {t.roServicesSubtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="group relative bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-2 border-blue-100 hover:border-blue-300"
            >
              {/* Water Wave Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/10 group-hover:to-cyan-500/10 transition-all duration-500 z-0"></div>
              
              <div className="relative z-10">
                <div className="relative h-72 overflow-hidden bg-gradient-to-br from-blue-100 to-cyan-100">
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-200 to-cyan-200">
                    <div className="text-center">
                      <div className="text-6xl mb-4">{service.icon}</div>
                      <div className="text-blue-700 font-semibold text-lg">{service.title}</div>
                    </div>
                  </div>
                  {/* Water Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-blue-600/40 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm border-2 border-blue-500/50 rounded-xl p-4 shadow-lg transform group-hover:scale-110 transition-all duration-300">
                    <span className="text-4xl">{service.icon}</span>
                  </div>
                  
                  {/* Title Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg">
                      {service.title}
                    </h3>
                  </div>
                </div>
                
                <div className="p-8 bg-white">
                  <p className="text-gray-600 mb-6 leading-relaxed text-base">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center group/item">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover/item:scale-110 transition-transform shadow-lg">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* CTA Button */}
                  <button className="mt-8 w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform group-hover:scale-105 shadow-lg">
                    {language === 'en' ? 'Learn More' : 'अधिक जानें'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <div className="relative mt-20">
          <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 rounded-3xl p-8 md:p-16 text-white shadow-2xl overflow-hidden">
            {/* Water Wave Pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320" preserveAspectRatio="none">
                <path fill="rgba(255, 255, 255, 0.1)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
              </svg>
            </div>
            
            <div className="relative z-10 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                  </svg>
                  <h3 className="text-4xl md:text-5xl font-extrabold">
                    {t.roServicesWhyTitle}
                  </h3>
                </div>
                <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                  {t.roServicesWhySubtitle}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: '⚡',
                    title: t.roServicesWhy1,
                    description: t.roServicesWhy1Desc,
                    gradient: 'from-blue-400 to-cyan-400'
                  },
                  {
                    icon: '🔧',
                    title: t.roServicesWhy2,
                    description: t.roServicesWhy2Desc,
                    gradient: 'from-cyan-400 to-blue-400'
                  },
                  {
                    icon: '✅',
                    title: t.roServicesWhy3,
                    description: t.roServicesWhy3Desc,
                    gradient: 'from-blue-400 to-cyan-500'
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 transform hover:scale-105 transition-all duration-300">
                    <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto text-4xl shadow-lg`}>
                      {item.icon}
                    </div>
                    <h4 className="text-2xl font-bold mb-3 text-center">{item.title}</h4>
                    <p className="text-blue-100 text-center">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROServices;

